import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  AppInput,
  AppSelect,
  OutlineButton,
  PrimaryButton,
  SortableTh,
  StatusAlertCard,
  StatusBadge,
} from '../../../../components/ui';
import { useTableSort } from '../../../../hooks/useTableSort';
import {
  operationalBadge,
  resolveAssetOwner,
} from '../../../../data/master/assetOwner';
import { listEquipment } from '../../../../repositories/equipment.repository';
import { recommendIndent } from '../../../../repositories/indent.repository';
import { getConfigValue } from '../../../../repositories/systemConfig.repository';

/**
 * Pick available vehicles to recommend for an indent.
 * API recommend is one equipment per call: { equipmentId, remark }.
 */
export function RecommendAssetsModal({
  isOpen,
  onClose,
  indent,
  onSaved,
  maxRecommendations,
}) {
  const toast = useToast();
  const [selected, setSelected] = useState(() => new Set());
  const [remarks, setRemarks] = useState({});
  const [typeFilter, setTypeFilter] = useState('match');
  const [candidates, setCandidates] = useState([]);
  const [maxTotal, setMaxTotal] = useState(maxRecommendations || 3);
  const [saving, setSaving] = useState(false);

  const remarkRequired = false;
  const remarkMaxLength = 250;
  const existingCount = indent?.recommendations?.length || 0;
  const slots = Math.max(0, maxTotal - existingCount);

  useEffect(() => {
    if (!isOpen || !indent) return;
    setSelected(new Set());
    setRemarks({});
    const hasType = Boolean(indent.line?.equipmentType || indent.line?.equipmentTypeId);
    setTypeFilter(hasType ? 'match' : 'all');
    let cancelled = false;
    (async () => {
      try {
        const [assetRes, cfgMax] = await Promise.all([
          listEquipment({
            pageSize: 200,
            kycStatus: 'verified',
            operationalStatus: 'available',
          }),
          maxRecommendations != null
            ? Promise.resolve(maxRecommendations)
            : getConfigValue('maxRecommendationsPerRequest'),
        ]);
        if (cancelled) return;
        setMaxTotal(Number(cfgMax) || 3);
        const already = new Set((indent.recommendations || []).map((r) => r.assetId));
        const items = (assetRes.items || []).filter((a) => {
          if (a.status !== 'Verified') return false;
          if ((a.operationalStatus || 'Available') === 'Deployed') return false;
          if (already.has(a.assetId)) return false;
          if (a.vendorId && indent.vendorId && a.vendorId === indent.vendorId) return false;
          if (
            a.vendorDbId != null &&
            indent.vendorDbId != null &&
            Number(a.vendorDbId) === Number(indent.vendorDbId)
          ) {
            return false;
          }
          return true;
        });
        setCandidates(items);
      } catch (err) {
        if (!cancelled) {
          toast({
            title: err?.message || 'Failed to load equipment',
            status: 'error',
            position: 'top-right',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, indent, maxRecommendations, toast]);

  const requestTypeLabel = indent?.line?.equipmentType || 'selected type';

  const filteredCandidates = useMemo(() => {
    if (!indent) return [];
    if (typeFilter === 'match') {
      const typeName = indent.line?.equipmentType;
      const typeId = indent.line?.equipmentTypeId;
      return candidates.filter((a) => {
        if (typeName && a.equipmentType === typeName) return true;
        if (typeId != null && Number(a.equipmentTypeId) === Number(typeId)) return true;
        return false;
      });
    }
    return candidates;
  }, [candidates, typeFilter, indent]);

  const allRows = useMemo(
    () =>
      filteredCandidates.map((asset) => ({
        asset,
        owner: resolveAssetOwner(asset),
      })),
    [filteredCandidates],
  );

  const { sorted: rows, sort, toggleSort } = useTableSort(allRows, {
    assetId: (r) => r.asset.assetId,
    equipment: (r) => r.asset.equipmentDescription || r.asset.equipmentType,
    owner: (r) => r.owner.ownerName,
    village: (r) => r.owner.villageName,
    operational: (r) => r.asset.operationalStatus,
  });

  if (!indent) return null;

  const toggle = (assetId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(assetId)) {
        next.delete(assetId);
        return next;
      }
      if (next.size >= slots) {
        toast({
          title: `Maximum ${maxTotal} recommendations per request`,
          description:
            slots === 0
              ? 'This request already has the maximum.'
              : `You can add ${slots} more for this request.`,
          status: 'warning',
          position: 'top-right',
        });
        return prev;
      }
      next.add(assetId);
      return next;
    });
  };

  const setRemark = (assetId, value) => {
    setRemarks((prev) => ({ ...prev, [assetId]: value }));
  };

  const handleRecommend = async () => {
    const ids = [...selected];
    if (ids.length < 1) {
      toast({
        title: 'Select at least one vehicle',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    if (remarkRequired && ids.some((id) => !(remarks[id] || '').trim())) {
      toast({
        title: 'Recommendation remark required',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    if (indent.id == null) {
      toast({ title: 'Missing indent id', status: 'error', position: 'top-right' });
      return;
    }

    setSaving(true);
    try {
      for (const assetId of ids) {
        const asset = candidates.find((a) => a.assetId === assetId);
        if (!asset?.id) continue;
        await recommendIndent(indent.id, {
          equipmentId: asset.id,
          remark: remarks[assetId] || '',
        });
      }
      toast({
        title: `${ids.length} recommendation(s) added`,
        description: 'Shown under Recommended equipment. Assign after offline confirm.',
        status: 'success',
        position: 'top-right',
      });
      onSaved?.();
      onClose();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to recommend',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      isCentered
      scrollBehavior="inside"
      trapFocus={false}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        my={4}
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader pb={2}>
          Select available vehicles
          <Text fontSize="14px" fontWeight="500" color="text.secondary" mt={1}>
            Request: {requestTypeLabel} · {indent.indentId}. Maximum {maxTotal}{' '}
            recommendations per request — add a remark for each pick.
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" overflowY="auto" minH={0} pb={4}>
          <VStack align="stretch" spacing={4}>
            <Flex gap={3} wrap="wrap" align="end">
              <Box minW={{ base: '100%', sm: '280px' }}>
                <AppSelect
                  label="Matching type"
                  value={typeFilter}
                  onChange={(v) => setTypeFilter(v || 'match')}
                  options={[
                    {
                      value: 'match',
                      label: indent.line?.equipmentType
                        ? `Same as request · ${indent.line.equipmentType}`
                        : 'Same as request type',
                    },
                    { value: 'all', label: 'All available (any type)' },
                  ]}
                />
              </Box>
              <Text fontSize="13px" color="text.secondary" pb={2}>
                {rows.length} available · {selected.size} of {slots} slot
                {slots === 1 ? '' : 's'} used
              </Text>
            </Flex>

            {slots === 0 ? (
              <StatusAlertCard
                title={`Recommendation limit reached (${maxTotal})`}
                body="Remove an existing recommendation on the request before adding another."
              />
            ) : null}

            <Box
              borderWidth="1px"
              borderColor="border.subtle"
              borderRadius="md"
              overflow="hidden"
            >
              <Box overflowX="auto">
                <Table variant="simple" size="md">
                  <Thead bg="bg.input">
                    <Tr>
                      <Th w="56px" px={3} />
                      <SortableTh columnKey="assetId" sort={sort} onSort={toggleSort}>
                        Asset
                      </SortableTh>
                      <SortableTh columnKey="equipment" sort={sort} onSort={toggleSort}>
                        Equipment
                      </SortableTh>
                      <SortableTh columnKey="owner" sort={sort} onSort={toggleSort}>
                        Owner
                      </SortableTh>
                      <SortableTh columnKey="village" sort={sort} onSort={toggleSort}>
                        Village
                      </SortableTh>
                      <SortableTh columnKey="operational" sort={sort} onSort={toggleSort}>
                        Status
                      </SortableTh>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map(({ asset, owner }) => {
                      const isOn = selected.has(asset.assetId);
                      const isFull = !isOn && selected.size >= slots;
                      return (
                        <React.Fragment key={asset.assetId}>
                          <Tr
                            cursor={isFull ? 'not-allowed' : 'pointer'}
                            opacity={isFull ? 0.5 : 1}
                            bg={isOn ? 'bg.amberTint' : undefined}
                            _hover={{ bg: isOn ? 'bg.amberTint' : 'bg.input' }}
                            onClick={() => toggle(asset.assetId)}
                          >
                            <Td w="56px" px={3} onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                size="lg"
                                isChecked={isOn}
                                isDisabled={isFull}
                                onChange={() => toggle(asset.assetId)}
                                colorScheme="yellow"
                              />
                            </Td>
                            <Td fontFamily="mono" fontSize="13px">
                              {asset.assetId}
                            </Td>
                            <Td>
                              <Text fontWeight="600" fontSize="14px">
                                {asset.equipmentDescription || asset.equipmentType}
                              </Text>
                              <Text fontSize="12px" color="text.secondary">
                                {[asset.manufacturer, asset.model].filter(Boolean).join(' ')}
                                {asset.regNo ? ` · ${asset.regNo}` : ''}
                              </Text>
                            </Td>
                            <Td>
                              <StatusBadge
                                status={owner.ownerType === 'Vendor' ? 'info' : 'pending'}
                              >
                                {owner.ownerType}
                              </StatusBadge>
                              <Text fontWeight="600" fontSize="13px" mt={1}>
                                {owner.ownerName}
                              </Text>
                            </Td>
                            <Td fontSize="13px">{owner.villageName || '—'}</Td>
                            <Td>
                              <StatusBadge status={operationalBadge(asset.operationalStatus)}>
                                {asset.operationalStatus}
                              </StatusBadge>
                            </Td>
                          </Tr>
                          {isOn ? (
                            <Tr bg="bg.amberTint">
                              <Td />
                              <Td colSpan={5} pt={0} pb={4}>
                                <AppInput
                                  label="Recommendation remark"
                                  isRequired={remarkRequired}
                                  value={remarks[asset.assetId] || ''}
                                  onChange={(e) =>
                                    setRemark(asset.assetId, e.target.value)
                                  }
                                  maxLength={remarkMaxLength}
                                  placeholder="On whose reference / why this machine"
                                />
                              </Td>
                            </Tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                    {rows.length === 0 ? (
                      <Tr>
                        <Td colSpan={6}>
                          <Text color="text.secondary" py={8} textAlign="center">
                            No available verified vehicles
                            {typeFilter === 'match'
                              ? ` for ${requestTypeLabel}`
                              : ''}
                            . Try “All available (any type)” if needed.
                          </Text>
                        </Td>
                      </Tr>
                    ) : null}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2} borderTopWidth="1px" borderColor="border.subtle">
          <OutlineButton type="button" onClick={onClose} isDisabled={saving}>
            Cancel
          </OutlineButton>
          <PrimaryButton
            type="button"
            onClick={handleRecommend}
            isDisabled={selected.size < 1}
            isLoading={saving}
          >
            Recommend ({selected.size}/{maxTotal})
          </PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
